import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import "./Tabela.css";

/**
 * Tabela que vira **cartões** no celular.
 *
 * <h2>Por que não é só rolagem horizontal</h2>
 *
 * Era. Uma tabela de sete colunas dentro de `overflow-x: auto` cabe no código e
 * não cabe na mão: para ver a validade é preciso rolar até perder de vista o
 * código do lote — e aí a leitura passa a depender de memória. Numa tela que
 * decide o que fazer com um material, ler a linha errada é o modo de falha.
 *
 * <p>Abaixo de 52rem cada linha vira um cartão, com o valor **principal** em
 * destaque e os demais como pares rótulo/valor. Nada some, nada rola de lado.
 *
 * <h2>A semântica não se perde no caminho</h2>
 *
 * `display: block` numa `<table>` faz o navegador **descartar a semântica de
 * tabela** — o leitor de tela deixa de anunciar linha e coluna, e quem depende
 * dele perde exatamente a informação que o cartão dá de graça a quem enxerga.
 *
 * <p>Por isso os `role` são declarados explicitamente: eles sobrevivem à
 * mudança de `display` e valem nos dois tamanhos.
 *
 * <h2>Ordenar por coluna — e a tabela NÃO ordena nada</h2>
 *
 * <p>O cabeçalho vira botão nas colunas marcadas `ordenavel`, e o clique só
 * <b>avisa</b>: quem reordena é o servidor. Não é preciosismo de arquitetura —
 * a quantidade de insumo atravessa a rede como <b>texto</b> (`"120.000000"`)
 * para não perder casas decimais, e ordenar texto põe 120 antes de 25 numa
 * coluna que decide o que vai para a formulação.
 *
 * <p>O estado da ordem também é de quem chama, e por isso ele sobrevive a
 * recarregar a página se a tela o guardar no endereço. Uma tabela que guarda a
 * própria ordem esquece-a a cada montagem.
 */

/**
 * A largura em que a linha vira cartão — <b>na tela</b>, e a mesma consulta
 * que `ui.css` escreve.
 *
 * <p>⚠️ Ela está escrita nos dois lugares porque uma folha de estilo não se lê
 * do JavaScript sem custo, e um `@media` não se pergunta em React. O que
 * impede os dois de divergirem não é disciplina: `Tabela.ordenacao.teste.tsx`
 * lê `ui.css` do disco e reprova se a consulta daqui não for a de lá.
 *
 * <p>E ela existe por um motivo estreito, não por gosto de responsividade: no
 * modo cartão o `<thead>` fica <b>escondido do olho e presente no leitor</b>
 * (`clip-path: inset(50%)`), e um <b>botão</b> dentro dele seria um controle
 * focável de 1px no canto da tela — quem navega por teclado pararia num alvo
 * invisível. Onde a tabela é cartão, o cabeçalho volta a ser texto.
 *
 * <h2>🔴 `screen and` é o que faz os dois mecanismos concordarem NO PAPEL</h2>
 *
 * <p>Eles mediam coisas diferentes: o `@media` do CSS avalia a <b>caixa da
 * página</b>, e este `matchMedia` avalia a <b>janela</b>. Numa A4 em retrato a
 * caixa útil tem 194 mm ≈ 733 px e cai abaixo de 52rem — o CSS virava cartão
 * (e recortava o `<thead>`) enquanto `emCartao` continuava `false` numa janela
 * de 1280, ou seja, botões de ordenação desenhados dentro de um cabeçalho
 * invisível. Em paisagem a caixa passa dos 52rem e o mesmo papel saía tabela.
 *
 * <p>O JavaScript <b>não</b> tem como enxergar a caixa da página, então a
 * correção é do outro lado: com `screen and`, no papel os dois respondem
 * <b>não</b> — o CSS porque a mídia não é `screen`, e este `matchMedia` pela
 * mesma razão. Paper não é mão: não tem dedo nem rolagem, que são os dois
 * motivos de o cartão existir. O bloco de `ui.css` traz a decisão por extenso.
 */
export const LARGURA_DE_CARTAO = "screen and (max-width: 52rem)";

/** O sentido de uma ordenação, no vocabulário da casa. */
export type SentidoDaOrdem = "crescente" | "decrescente";

/**
 * Por qual coluna a tabela está ordenada — e em que sentido.
 *
 * <p>É <b>estado de quem chama</b>, não do componente. A tabela não reordena
 * nada: numa lista de material a ordem é do servidor (a quantidade viaja como
 * texto, e `"120.000000"` vem antes de `"25.000000"` em qualquer comparação de
 * texto). O que ela faz é <b>dizer</b> qual é a ordem e oferecer o gesto de
 * trocá-la.
 */
export interface OrdemDaTabela {
  /** A `chave` da coluna ordenada. */
  chave: string;
  sentido: SentidoDaOrdem;
}

export interface Coluna<T> {
  chave: string;
  rotulo: string;

  /**
   * O cabeçalho vira botão e a coluna passa a poder ordenar a lista.
   *
   * <p>Só tem efeito com `aoOrdenar` — sem quem atenda, um cabeçalho clicável
   * é um gesto que não faz nada, e gesto que não faz nada numa tela de
   * conferência é gesto que faz duvidar de que a tela respondeu.
   */
  ordenavel?: boolean;

  /** Alinha à direita e usa numeral tabular — quantidade, contagem. */
  numerica?: boolean;

  /**
   * Fonte monoespaçada — para código que se confere caractere a caractere.
   *
   * <p>É do código, não do destaque: `MP-0002` alinha e se lê em coluna;
   * uma razão social em mono só fica mais larga.
   */
  mono?: boolean;

  /**
   * Vira o **título do cartão** no celular, sem repetir o rótulo.
   *
   * <p>É o que identifica a linha: o código do lote, o nome do fornecedor.
   * Sem isso, o cartão começa por "Lote: MP-0001" — rótulo redundante ocupando
   * a linha mais visível de todas.
   */
  principal?: boolean;

  /**
   * Cabeçalho existe para o leitor de tela, mas não é desenhado.
   *
   * <p>Para a coluna de ação, cujo rótulo visível seria ruído.
   */
  rotuloOculto?: boolean;

  conteudo(linha: T): ReactNode;
}

interface Props<T> {
  colunas: readonly Coluna<T>[];
  linhas: readonly T[];
  chave(linha: T): string;
  /** Aparece abaixo da tabela e é a descrição acessível dela. */
  legenda?: ReactNode;
  /**
   * Qual coluna manda na ordem <b>hoje</b> — `null` é "a ordem padrão de quem
   * serve a lista", e não "nenhuma ordem".
   */
  ordem?: OrdemDaTabela | null | undefined;
  /**
   * A pessoa pediu para ordenar por esta coluna. Quem decide o que isso
   * significa — alternar sentido, refazer a consulta — é quem chama.
   */
  aoOrdenar?: ((chave: string) => void) | undefined;
}

/**
 * A tabela está desenhada como cartão?
 *
 * <p>Padrão `false`, e o padrão importa: onde não há `matchMedia` — o jsdom dos
 * testes, uma renderização no servidor — a tabela é <b>tabela</b>. O modo
 * cartão é o caso especial, e caso especial não se assume por ausência de
 * informação.
 */
function usarModoCartao(): boolean {
  const [cartao, definir] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;

    const consulta = window.matchMedia(LARGURA_DE_CARTAO);
    const aplicar = () => definir(consulta.matches);
    aplicar();
    consulta.addEventListener("change", aplicar);
    return () => consulta.removeEventListener("change", aplicar);
  }, []);

  return cartao;
}

export function Tabela<T>({ colunas, linhas, chave, legenda, ordem, aoOrdenar }: Props<T>) {
  const emCartao = usarModoCartao();
  const atual = ordem ?? null;

  return (
    <table className="ui-tabela" role="table">
      {legenda !== undefined && (
        <caption className="ui-tabela__legenda">{legenda}</caption>
      )}
      <thead className="ui-tabela__cabecalho" role="rowgroup">
        <tr role="row">
          {colunas.map((coluna) => {
            const ordenavel =
              coluna.ordenavel === true && aoOrdenar !== undefined && !emCartao;
            const ativa = ordenavel && atual !== null && atual.chave === coluna.chave;

            return (
              <th
                key={coluna.chave}
                scope="col"
                role="columnheader"
                className={coluna.numerica === true ? "ui-tabela__numero" : undefined}
                /* `aria-sort` no `th`, e não no botão: quem ouve a tela recebe
                   o estado ao entrar na COLUNA, junto do nome dela. Só a
                   coluna ativa se diz ordenada — "none" nas outras diz que
                   dá para ordenar e que ainda não é por elas. */
                aria-sort={
                  !ordenavel
                    ? undefined
                    : ativa
                      ? atual.sentido === "crescente"
                        ? "ascending"
                        : "descending"
                      : "none"
                }
              >
                {coluna.rotuloOculto === true ? (
                  <span className="ui-tabela__so-leitor">{coluna.rotulo}</span>
                ) : ordenavel ? (
                  <button
                    type="button"
                    className="ui-tabela__ordenar"
                    onClick={() => aoOrdenar(coluna.chave)}
                  >
                    {coluna.rotulo}
                    {/* A seta DESENHA o que o `aria-sort` já diz. Em
                        `aria-hidden` para não virar "seta para cima" lido
                        depois de "ordenado crescente". */}
                    <span className="ui-tabela__seta" aria-hidden="true">
                      {ativa ? (atual.sentido === "crescente" ? "↑" : "↓") : "↕"}
                    </span>
                  </button>
                ) : (
                  coluna.rotulo
                )}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody role="rowgroup">
        {linhas.map((linha) => (
          <tr key={chave(linha)} className="ui-tabela__linha" role="row">
            {colunas.map((coluna) => (
              <td
                key={coluna.chave}
                role="cell"
                // O rótulo acompanha o dado no cartão. Vem da definição da
                // coluna, e não de um atributo escrito à mão em cada célula:
                // rótulo digitado uma vez por célula é rótulo que sai do lugar
                // quando alguém reordena as colunas.
                data-rotulo={coluna.rotuloOculto === true ? undefined : coluna.rotulo}
                className={
                  [
                    coluna.numerica === true ? "ui-tabela__numero" : "",
                    coluna.principal === true ? "ui-tabela__principal" : "",
                    coluna.mono === true ? "ui-tabela__mono" : "",
                  ]
                    .filter((c) => c !== "")
                    .join(" ") || undefined
                }
              >
                {coluna.conteudo(linha)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
